// **Approve brief** — the operator doing, for a client, what the client's own
// approve control inside the brief does: open the search gate and start the
// flow.
//
// It is the third client-facing write on the project page, and it sits with the
// other two in the header rather than inside the Brief tab, because the operator
// who needs it arrived from an alert about a client who is STUCK and should not
// have to find a tab first.
//
// Deliberately the loudest control on the page (solid danger, `lg`, its own
// block). The other two are small because they are reversible or invisible;
// this one is neither. It overrides the brief checklist and, a beat later,
// Mira is messaging real talent on someone's behalf. A button that does that
// should not look like a table action.
//
// **It ASKS which flow, because the client's own control does.** The button used
// to be a plain confirm that always started the concierge, which was honest when
// it was written and stopped being so once the client's control grew a choice:
// Mira hiring for them, or the plain search landing them on a shortlist they
// browse themselves. An operator pressing approve is standing in for that
// client, so they answer that same question rather than having it answered for
// them. Hence a lane dialog instead of a confirm: two equal cards, no default
// highlighted, because picking wrong is the cost here and speed is not.
//
// The dialog states what the CLIENT will experience, never what column moves —
// same rule as `operator-controls.tsx` — and names the checklist rows it is
// about to override, so "override the gate" is a number the operator can see
// rather than a phrase they have to trust.
//
// Support and viewer modes never render this: it hangs off the page's null
// `onChanged` read-only signal like every other write. The backend agrees
// independently (`OperatorWriteDep`: explorer OR talent_management), so a viewer
// is refused there too.
import { Button, Dialog, useToast } from "@recruiter/shared";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";

import {
  ApiError,
  type ApproveBriefLane,
  type BriefChecklistView,
  postApproveBrief,
} from "../lib/api";
import "./approve-brief-button.css";

/** What each lane does TO THE CLIENT, in the order the operator reads them.
 *
 *  Concierge first because it is the flow this button has always started, so an
 *  operator who pressed it last week finds the thing they expected in the place
 *  they expect it, with the alternative beside it rather than in its place. */
const LANES: { lane: ApproveBriefLane; title: string; body: string; note: string }[] = [
  {
    lane: "concierge",
    title: "Mira hires for them",
    body: "She reaches out to talent on the client's behalf, handles the replies, and brings back offers.",
    note: "Takes a while. The client can close the tab and come back to it.",
  },
  {
    lane: "search",
    title: "Show them matches now",
    body: "The search runs and the client lands on a shortlist of talent they browse and pick from themselves.",
    note: "Finishes in a moment. Nobody is contacted until the client picks.",
  },
];

export function ApproveBriefButton({
  projectId,
  checklist,
  onChanged,
}: {
  projectId: string;
  /** The client's brief checklist, when the payload carries one. Used only to
   *  say how much this approval is overriding; null just makes the dialog
   *  quieter, never wrong. */
  checklist: BriefChecklistView | null;
  /** Re-read the project. The refetched record is the source of truth. */
  onChanged: () => void;
}) {
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  // One pick per opening, latched in a REF rather than read off `isPending`:
  // two clicks landing in the same tick both see the pre-render pending state,
  // and here the two clicks can be on DIFFERENT lanes — whose in-flight guards
  // live in different tables server-side, so nothing downstream would stop one
  // gesture starting a concierge run AND a search. Same guard, and same reason,
  // as the client's own flow choice.
  const pickedRef = useRef(false);

  const openRequired = checklist
    ? Math.max(0, checklist.requiredTotal - checklist.requiredDone)
    : 0;

  const approve = useMutation({
    mutationFn: (lane: ApproveBriefLane) => postApproveBrief(projectId, lane),
    onSuccess: (result) => {
      closeDialog();
      // `started: false` is a SUCCESS, not a silent no-op: a run was already
      // going and the server adopted it rather than starting a second one
      // (double outreach is a money bug). Say which happened, or an operator
      // pressing twice cannot tell the difference. The LANE comes off the
      // response, not off what we asked for, so the toast reports the flow the
      // client is actually in.
      const conciergeLane = result.lane === "concierge";
      toast.add({
        title: result.started ? "Brief approved" : "Already running",
        description: result.started
          ? conciergeLane
            ? "Mira is reaching out to talent for this client now."
            : "The search is running. The client gets a shortlist to pick from."
          : conciergeLane
            ? "This client already had Mira hiring for them, so nothing new was started."
            : "This client already had a search running, so nothing new was started.",
        data: { tone: "success" },
      });
      onChanged();
    },
    onError: (error: unknown) => {
      closeDialog();
      // Surfaced, never swallowed (doctrine 1.5). The 409 an operator actually
      // meets is the takeover gate: either a human already has this project, or
      // the committed budget was big enough that approving ran the big-client
      // Stop instead of a search.
      toast.add({
        title: "Couldn't approve the brief",
        description: error instanceof ApiError ? error.detail : undefined,
        data: { tone: "danger" },
      });
    },
  });

  function closeDialog() {
    pickedRef.current = false;
    setAsking(false);
  }

  function pick(lane: ApproveBriefLane) {
    if (pickedRef.current) return;
    pickedRef.current = true;
    approve.mutate(lane);
  }

  return (
    <div className="abt">
      <Button
        variant="solid"
        tone="danger"
        size="lg"
        className="abt__button"
        disabled={approve.isPending}
        onClick={() => {
          pickedRef.current = false;
          setAsking(true);
        }}
      >
        {approve.isPending ? "Approving…" : "Approve brief"}
      </Button>
      <p className="abt__hint">
        {openRequired > 0
          ? `Starts the flow you pick, over ${openRequired} unanswered required ${
              openRequired === 1 ? "question" : "questions"
            }.`
          : "Starts the flow you pick, as if the client approved it themselves."}
      </p>

      <Dialog.Root
        open={asking}
        onOpenChange={(next) => {
          // Dismissing is a CANCEL: nothing has run yet, and the operator gets
          // the button back untouched. Suppressed mid-request so an in-flight
          // approve is not yanked out from under itself.
          if (!next && !approve.isPending) closeDialog();
        }}
      >
        <Dialog.Content className="abt__dialog">
          <Dialog.Title>Where should this client go?</Dialog.Title>
          <Dialog.Description>
            {openRequired > 0
              ? `Approving starts the flow you pick straight away, as if the client had approved the brief themselves. Their brief still has ${openRequired} required ${
                  openRequired === 1 ? "question" : "questions"
                } unanswered, and this goes ahead anyway.`
              : "Approving starts the flow you pick straight away, as if the client had approved the brief themselves."}
          </Dialog.Description>
          {/* Two equal cards, neither styled as the primary: the point is a real
              choice, and the operator is answering for someone who is not here
              to be asked. Whole-card buttons, stacked, so the whole trade-off is
              the click target rather than a small label at the bottom of it. */}
          <div className="abt__lanes">
            {LANES.map((option) => (
              <button
                key={option.lane}
                type="button"
                className="abt__lane"
                disabled={approve.isPending}
                onClick={() => pick(option.lane)}
              >
                <span className="abt__laneTitle">{option.title}</span>
                <span className="abt__laneBody">{option.body}</span>
                <span className="abt__laneNote">{option.note}</span>
              </button>
            ))}
          </div>
          <div className="abt__actions">
            <Button
              variant="ghost"
              tone="neutral"
              disabled={approve.isPending}
              onClick={closeDialog}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
